import React, { createContext, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { UrlSearchParams } from "types/enums";

export const useOpenModalFromCard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return (item: { type: string; id: string }, autoplay?: boolean) => {
    const params = new URLSearchParams(location.search);
    params.set(UrlSearchParams.ContentType, item.type);
    params.set(UrlSearchParams.ContentId, item.id);
    if (autoplay) {
      params.set(UrlSearchParams.Autoplay, "true");
    }

    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };
};

const ContentModalManagerContext = createContext<{
  contentType: string | null;
  contentId: string | null;
  closeModal: () => void;
}>({
  contentType: null,
  contentId: null,
  closeModal: () => {},
});

export const useContentModalManager = () =>
  useContext(ContentModalManagerContext);

export const ContentModalManagerProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const contentType = params.get(UrlSearchParams.ContentType);
  const contentId = params.get(UrlSearchParams.ContentId);

  const closeModal = React.useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete(UrlSearchParams.ContentType);
    params.delete(UrlSearchParams.ContentId);
    params.delete(UrlSearchParams.Autoplay);
    const newSearch = params.toString();
    navigate(
      newSearch ? `${location.pathname}?${newSearch}` : location.pathname,
      { replace: true }
    );
  }, [navigate, location.pathname, location.search]);

  const value = useMemo(
    () => ({ contentType, contentId, closeModal }),
    [contentType, contentId, closeModal]
  );

  return (
    <ContentModalManagerContext.Provider value={value}>
      {children}
    </ContentModalManagerContext.Provider>
  );
};
